import BaseApi from '@/api/Base/BaseApi'
import {
  AddProjectMembersPayload,
  ConnectEdgePayload,
  CopyPasteNodePayload,
  CopyPasteNodeResponse,
  CreateProjectPayload,
  DisconnectEdgePayload,
  EditProjectPayload,
  GetProjectListPayload,
  GetProjectListResponse,
  ProjectDetail,
  ProjectType,
  RemoveProjectMemberPayload,
  UpdateMemberPermissionPayload,
  UpdateNodePositionPayload,
  UpdateProjectThumbnailPayload,
  Edge,
  Node,
  DeleteNodePayload,
  Pipeline,
  UpdateMemberPermissionResponse,
  UpdateNodeNamePayload
} from '@/api/Project/types'
import { UserInfo } from '@/api/User/types'

const ROOT = '/project'
const COUNT_PER_PAGE = 20

export default class ProjectApi extends BaseApi {
  getProjectList(payload: GetProjectListPayload) {
    const { page, itemsPerPage, sortKey, sortDir, workspaceId, searchKeyword } = payload
    return this.agent.get<GetProjectListResponse>(`/workspace/${workspaceId}/project/list`, {
      params: {
        page,
        num_per_page: itemsPerPage ?? COUNT_PER_PAGE,
        sort_key: sortKey,
        sort_dir: sortDir,
        searchKeyword,
      },
    })
  }

  getProjectDetail(projectId: string) {
    return this.agent.get<ProjectDetail>(`${ROOT}/${projectId}`)
  }

  createProject(payload: CreateProjectPayload) {
    return this.agent.post<ProjectType>(`/workspace/${payload.workspaceId}/project`, payload)
  }

  deleteProject(projectId: string) {
    return this.agent.delete(`${ROOT}/${projectId}`)
  }

  editProject(payload: EditProjectPayload) {
    const { projectId, name, ...rest } = payload
    return this.agent.put(`${ROOT}/${projectId}`, { ...rest, name: name?.trim() })
  }

  updateProjectThumbnail(payload: UpdateProjectThumbnailPayload) {
    const { projectId, file, isUserUploadedThumbnail } = payload
    const formData = new FormData()
    formData.append('file', file)
    if (isUserUploadedThumbnail) {
      formData.append('isUserUploadedThumbnail', 'true')
    }
    return this.agent.put(`${ROOT}/${projectId}/thumbnail`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  getProjectMembers(projectId: string) {
    return this.agent.get<UserInfo[]>(`${ROOT}/${projectId}/member/list`)
  }

  getProjectMemberRole(projectId: string) {
    return this.agent.get(`${ROOT}/${projectId}/role`)
  }

  addProjectMembers(payload: AddProjectMembersPayload) {
    const { projectId, ...rest } = payload
    return this.agent.post(`${ROOT}/${projectId}/members`, rest)
  }

  updateMemberPermission(payload: UpdateMemberPermissionPayload) {
    const { projectId, ...rest } = payload
    return this.agent.put<UpdateMemberPermissionResponse>(`${ROOT}/${projectId}/member`, rest)
  }

  removeProjectMember(payload: RemoveProjectMemberPayload) {
    const { projectId, userId } = payload
    return this.agent.delete(`${ROOT}/${projectId}/member/${userId}`)
  }

  copyPasteNode(payload: CopyPasteNodePayload) {
    const { projectId, ...rest } = payload
    return this.agent.post<CopyPasteNodeResponse>(`${ROOT}/${projectId}/copy-node`, rest)
  }

  updateNodePosition(payload: UpdateNodePositionPayload) {
    const { projectId, nodes } = payload
    return this.agent.put<Node>(`${ROOT}/${projectId}/position`, nodes)
  }

  getAllEdges(projectId: string, isOpsView: boolean) {
    return this.agent.get<Edge[]>(`${ROOT}/${projectId}/edges`, {
      params: {
        isOpsView,
      },
    })
  }

  connectEdge(payload: ConnectEdgePayload) {
    const { projectId, ...rest } = payload
    return this.agent.put<Edge[]>(`${ROOT}/${projectId}/connect-edge`, rest)
  }

  disconnectEdge(payload: DisconnectEdgePayload) {
    const { projectId, ...rest } = payload
    return this.agent.put<Edge[]>(`${ROOT}/${projectId}/disconnect-edge`, rest)
  }

  deleteNode(payload: DeleteNodePayload) {
    const { projectId, nodeId, isOpsNode } = payload
    return this.agent.delete<Pipeline>(`${ROOT}/${projectId}/delete-node/${nodeId}`, { data: { isOpsNode } })
  }

  updateNodeName(payload: UpdateNodeNamePayload) {
    const { projectId, name, nodeId, nodeType } = payload
    return this.agent.put(`${ROOT}/${projectId}/node-name`, {
      name: name?.trim(),
      nodeId,
      nodeType,
    })
  }
}
